import { useState } from "react";
import { useToast } from "@/components/ui/ToastProvider";
import type { TileSet } from "@west-marches/shared";
import {
  TILE_CATALOG,
  getTileById,
  getTaxonomyNodeById,
  getOfficialRootTaxonomyNodes,
  getTileUrl,
  isCustomTileFilenameInTileSet,
  isTileSetEnabled,
  removeCustomTileAtRuntime,
  registerCustomTileAtRuntime,
  useTileCatalogStore,
} from "@west-marches/shared";
import { preloadTiles } from "@west-marches/shared";
import { apiFetch } from "@/lib/apiClient";
import { getMapChromeTheme } from "@/components/mapChromeTheme";

type Step = "pick-ref" | "compose" | "generating" | "preview" | "saving";

interface Props {
  taxonomyNodeId: string;
  taxonomyLabel: string;
  tileSets: TileSet[];
  onClose: () => void;
}

function slugifyTaxonomySegment(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export default function TileGenerator({
  taxonomyNodeId,
  taxonomyLabel,
  tileSets,
  onClose,
}: Props) {
  const theme = getMapChromeTheme();
  const toast = useToast();
  const catalogRevision = useTileCatalogStore((s) => s.revision);
  const [step, setStep] = useState<Step>("pick-ref");
  const [selectedRef, setSelectedRef] = useState<string | null>(null);
  const [iterateBase64, setIterateBase64] = useState<string | null>(null);
  const [prompt, setPrompt] = useState("");
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);

  const [tileName, setTileName] = useState("");
  const [saveTaxonomyNodeId, setSaveTaxonomyNodeId] = useState(taxonomyNodeId);
  const [addToExisting, setAddToExisting] = useState(false);
  const [existingTileSetId, setExistingTileSetId] = useState("");
  const [saving, setSaving] = useState(false);
  const [deletingFilename, setDeletingFilename] = useState<string | null>(null);
  const [hiddenFilenames, setHiddenFilenames] = useState<string[]>([]);
  const rootTaxonomyNodes = getOfficialRootTaxonomyNodes();
  const saveTargets =
    rootTaxonomyNodes.length > 0
      ? rootTaxonomyNodes
      : [
          {
            id: taxonomyNodeId,
            parentId: null,
            label: taxonomyLabel,
            isOfficial: true,
            tileSetIds: [],
          },
        ];
  const activeSaveTaxonomyNode =
    saveTargets.find((node) => node.id === saveTaxonomyNodeId) ??
    getTaxonomyNodeById(saveTaxonomyNodeId);
  const saveTaxonomyLabel = slugifyTaxonomySegment(
    activeSaveTaxonomyNode?.label ?? taxonomyLabel,
  );

  const referenceTiles = tileSets
    .flatMap((group) =>
      group.tileIds.map((tileId, index) => ({
        tileSetId: group.id,
        tileId,
        filename: group.filenames[index],
        isCustom: isCustomTileFilenameInTileSet(group, group.filenames[index]),
      })),
    )
    .filter(
      (tile) => tile.filename && !hiddenFilenames.includes(tile.filename),
    );

  async function handleGenerate() {
    if (!prompt.trim() || (!selectedRef && !iterateBase64)) return;
    setStep("generating");

    try {
      const body: Record<string, unknown> = { prompt: prompt.trim() };
      if (iterateBase64) {
        body.referenceImages = [iterateBase64];
      } else if (selectedRef) {
        body.referenceTileIds = [selectedRef];
      }

      const res = await apiFetch("/api/generate-tile", {
        method: "POST",
        body: JSON.stringify(body),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Generation failed");

      setGeneratedImage(data.image);
      setStep("preview");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Unknown error");
      setStep("compose");
    }
  }

  async function handleSave() {
    if (!generatedImage || !tileName.trim()) return;

    const isNewTileSet = !addToExisting || !existingTileSetId;
    const tileSetId = isNewTileSet ? undefined : existingTileSetId;
    const existingTileSet = tileSetId ? TILE_CATALOG.find((g) => g.id === tileSetId) : undefined;
    const variantNum = existingTileSet ? existingTileSet.filenames.length + 1 : 1;
    const tileSetLabel = isNewTileSet ? tileName.trim() : existingTileSet!.label;
    const filename = `Hex - ${tileSetLabel} (${saveTaxonomyLabel}) ${variantNum}.png`;

    setSaving(true);

    try {
      const res = await apiFetch("/api/save-tile", {
        method: "POST",
        body: JSON.stringify({
          imageBase64: generatedImage,
          filename,
          tileSetId,
          taxonomyNodeId: saveTaxonomyNodeId,
          tileSetLabel,
          existingTileSetId: isNewTileSet ? undefined : existingTileSetId,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Save failed");
      if (typeof data.tileSetId !== "string") {
        throw new Error("Save response did not include a tile set id");
      }

      const tileId = registerCustomTileAtRuntime({
        filename,
        tileSetId: data.tileSetId,
        taxonomyNodeIds: [saveTaxonomyNodeId],
        tileSetLabel,
        existingTileSetId: isNewTileSet ? undefined : existingTileSetId,
        tileId: data.tileId,
        relativePath: data.relativePath,
      });

      await preloadTiles([tileId]);
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteReferenceTile(
    tileSetId: string,
    tileId: string,
    filename: string,
  ) {
    const confirmed = window.confirm(`Delete "${filename}" from the custom tile catalog?`);
    if (!confirmed) return;

    setDeletingFilename(filename);

    try {
      const res = await apiFetch("/api/save-tile", {
        method: "DELETE",
        body: JSON.stringify({ tileSetId, tileId, filename }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Delete failed");

      removeCustomTileAtRuntime(tileSetId, tileId);
      setHiddenFilenames((current) => [...current, filename]);
      if (selectedRef === tileId) {
        setSelectedRef(null);
        setStep("pick-ref");
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setDeletingFilename(null);
    }
  }

  const groupsInSaveTaxonomy = TILE_CATALOG.filter(
    (g) => g.taxonomyNodeIds.includes(saveTaxonomyNodeId) && isTileSetEnabled(g),
  );

  return (
    <div data-catalog-revision={catalogRevision}>
      {/* Header */}
      <div className="relative mb-6 min-h-[88px]">
        <button
          onClick={onClose}
          className={`absolute left-0 top-0 flex h-10 items-center gap-1.5 rounded-lg px-4 text-sm font-medium transition-colors ${theme.pickerActionButton}`}
        >
          &larr; Back
        </button>
        <h2 className={`pt-14 text-lg font-semibold ${theme.textPrimary}`}>
          New {taxonomyLabel} Tile
        </h2>
      </div>
      {/* Step: pick-ref */}
      {step === "pick-ref" && (
        <>
          <p className={`mb-3 text-sm ${theme.textSubtle}`}>Select a reference tile</p>
          <div className="flex flex-wrap gap-3">
            {referenceTiles.map((tile) => (
              <div
                key={tile.tileId}
                className="relative w-[120px] h-[120px] shrink-0"
              >
                <button
                  onClick={() => {
                    setSelectedRef(tile.tileId);
                    setIterateBase64(null);
                    setStep("compose");
                  }}
                  className={`h-full w-full overflow-hidden rounded-xl transition-all ${theme.cardIdleRing}`}
                >
                  <img
                    src={getTileUrl(tile.tileId) ?? ""}
                    alt={tile.filename}
                    className="w-full h-full object-cover"
                    draggable={false}
                    loading="lazy"
                  />
                </button>
                {tile.isCustom && (
                  <button
                    type="button"
                    onClick={() =>
                      void handleDeleteReferenceTile(tile.tileSetId, tile.tileId, tile.filename)
                    }
                    disabled={deletingFilename === tile.filename}
                    className={theme.pickerDeleteButton}
                  >
                    {deletingFilename === tile.filename ? "..." : "Delete"}
                  </button>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {/* Step: compose / generating */}
      {(step === "compose" || step === "generating") && (
        <div className="max-w-lg">
          {/* Reference thumbnail */}
          <div className="flex items-center gap-4 mb-4">
            <div className={`h-[120px] w-[120px] shrink-0 overflow-hidden rounded-xl ${theme.cardIdleRing}`}>
              {iterateBase64 ? (
                <img
                  src={`data:image/png;base64,${iterateBase64}`}
                  alt="Generated reference"
                  className="w-full h-full object-cover"
                  draggable={false}
                />
              ) : selectedRef ? (
                <img
                  src={getTileUrl(selectedRef) ?? ""}
                  alt={getTileById(selectedRef)?.filename ?? selectedRef}
                  className="w-full h-full object-cover"
                  draggable={false}
                />
              ) : null}
            </div>
            <div className="flex flex-col gap-1">
              <span className={`text-sm ${theme.textSubtle}`}>
                {iterateBase64 ? "Iterating from generated" : "Reference tile"}
              </span>
              <button
                onClick={() => {
                  setIterateBase64(null);
                  setStep("pick-ref");
                }}
                className={`text-left text-sm transition-colors ${theme.textMuted} hover:text-neutral-950`}
              >
                Change reference
              </button>
            </div>
          </div>

          {/* Prompt */}
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Describe the tile you want..."
            rows={3}
            className={`mb-3 ${theme.textarea}`}
            disabled={step === "generating"}
          />

          {/* Generate button */}
          <button
            onClick={handleGenerate}
            disabled={step === "generating" || !prompt.trim()}
            className={`w-full py-3 text-sm font-semibold ${theme.primaryButton}`}
          >
            {step === "generating" ? "Generating..." : "Generate Tile"}
          </button>
        </div>
      )}

      {/* Step: preview */}
      {step === "preview" && generatedImage && (
        <div className="max-w-lg">
          <div className="flex justify-center mb-4">
            <img
              src={`data:image/png;base64,${generatedImage}`}
              alt="Generated tile"
              className="h-[280px] w-[280px] rounded-xl object-contain"
              draggable={false}
            />
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => {
                setStep("compose");
                handleGenerate();
              }}
              className={`flex-1 py-2.5 text-sm font-medium ${theme.secondaryButton}`}
            >
              Regenerate
            </button>
            <button
              onClick={() => {
                setIterateBase64(generatedImage);
                setGeneratedImage(null);
                setStep("compose");
              }}
              className={`flex-1 py-2.5 text-sm font-medium ${theme.secondaryButton}`}
            >
              Iterate
            </button>
            <button
              onClick={() => setStep("saving")}
              className={`flex-1 py-2.5 text-sm font-semibold ${theme.primaryButton}`}
            >
              Save
            </button>
          </div>
        </div>
      )}

      {/* Step: saving (inline form) */}
      {step === "saving" && generatedImage && (
        <div className="max-w-lg space-y-4">
          <div className="flex justify-center">
            <img
              src={`data:image/png;base64,${generatedImage}`}
              alt="Generated tile"
              className="h-[160px] w-[160px] rounded-xl object-contain"
              draggable={false}
            />
          </div>

          <div>
            <label className={`mb-1 block text-sm font-semibold ${theme.textPrimary}`}>
              Tile Name
            </label>
            <input
              value={tileName}
              onChange={(e) => setTileName(e.target.value)}
              placeholder="e.g. Mushroom Forest"
              autoFocus
              className={theme.input}
            />
          </div>

          <div>
            <label className={`mb-1 block text-sm font-semibold ${theme.textPrimary}`}>
              Taxonomy
            </label>
            <select
              value={saveTaxonomyNodeId}
              onChange={(e) => {
                setSaveTaxonomyNodeId(e.target.value);
                setExistingTileSetId("");
              }}
              className={theme.select}
            >
              {saveTargets.map((node) => (
                <option key={node.id} value={node.id}>
                  {node.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={`flex cursor-pointer items-center gap-2 ${theme.textPrimary}`}>
              <input
                type="checkbox"
                checked={addToExisting}
                onChange={(e) => {
                  setAddToExisting(e.target.checked);
                  if (!e.target.checked) setExistingTileSetId("");
                }}
                className={theme.checkbox}
              />
              <span className="text-sm">Add to existing tile set</span>
            </label>

            {addToExisting && (
              <select
                value={existingTileSetId}
                onChange={(e) => setExistingTileSetId(e.target.value)}
                className={`mt-2 ${theme.select}`}
              >
                <option value="">
                  Select a tile set...
                </option>
                {groupsInSaveTaxonomy.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.label}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="flex gap-3 pt-2">
            <button
              onClick={() => setStep("preview")}
              className={`flex-1 py-2.5 text-sm font-medium ${theme.secondaryButton}`}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !tileName.trim() || (addToExisting && !existingTileSetId)}
              className={`flex-1 py-2.5 text-sm font-semibold ${theme.primaryButton}`}
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
